import { motion } from "motion/react"
import { useEffect, useState } from "react"
import { Outlet, useLocation } from "react-router"

import { BuscadorModulos } from "@/componentes/buscador/BuscadorModulos"
import { BotonReportar } from "@/componentes/soporte/BotonReportar"
import { LimiteDeError } from "@/componentes/ui/LimiteDeError"
import { esTransportista } from "@/lib/autorizacion"
import { registrarMiga } from "@/lib/bitacora"
import { usePantallaAncha } from "@/lib/pantalla"
import { usarSesion } from "@/lib/sesion"
import { ENTRADA } from "@/lib/transiciones"
import { BarraLateral } from "./BarraLateral"
import { Encabezado } from "./Encabezado"
import { MenuMovil } from "./MenuMovil"

export function AppShell() {
  const ubicacion = useLocation()
  const tema = usarSesion((e) => e.tema)
  const usuario = usarSesion((e) => e.usuario)
  const veloFotos = usarSesion((e) => e.veloFotos)
  const claro = tema === "claro"
  // ⚠ **Por debajo de 1024 px el menú es un cajón**, no una columna: 224 px fijos
  // son el 70 % de un teléfono y dejaban el área de trabajo en una tira. Ver
  // `lib/pantalla.ts` y `MenuMovil.tsx`.
  const ancha = usePantallaAncha()
  const [menuAbierto, setMenuAbierto] = useState(false)

  // Girar la tableta a apaisado con el cajón abierto lo dejaría abierto y sin
  // botón que lo cierre, porque el encabezado deja de tenerlo.
  useEffect(() => {
    if (ancha) setMenuAbierto(false)
  }, [ancha])

  // ⚠ **La apariencia se aplica AQUÍ, con la sesión, y se deshace al salir.** El
  // acceso es oscuro sobre la fotografía en cualquier caso —la lámina viaja hasta
  // convertirse en el menú y tiene que ser el mismo material—, así que aplicarla en
  // `index.html` pintaría de claro la pantalla de acceso de quien la eligió. En la
  // raíz y no en este `div`: los modales y los menús se montan en un portal bajo
  // `body` y también tienen que seguirla.
  useEffect(() => {
    const raiz = document.documentElement
    raiz.dataset.tema = tema
    // El velo sobre el fondo. `FondoPuerto` lo lee de `--fondo-sombra`, así
    // que ajustarlo es cambiar una variable y no volver a montar el telón.
    raiz.style.setProperty("--fondo-sombra", `rgb(0 0 0 / ${veloFotos})`)
    return () => {
      raiz.dataset.tema = "oscuro"
      raiz.style.removeProperty("--fondo-sombra")
    }
  }, [tema, veloFotos])

  // ⚠ **La ruta se anota aquí y no en la bitácora.** Sólo el enrutador sabe cuándo
  // cambia; un oyente de `popstate` se perdería toda navegación hecha con `<Link>`,
  // que son casi todas, y las migas dirían que la persona no se movió de sitio.
  useEffect(() => {
    registrarMiga("RUTA", ubicacion.pathname)
  }, [ubicacion.pathname])

  return (
    /* ⚠ **Sin fondo propio: el del sistema lo pone `Escenario`**, que vive por
       encima del enrutador y sostiene la fotografía. Con un `background` aquí, el
       armazón taparía el telón y no se vería ni un píxel de foto.

       ⚠ **Paneles FLOTANTES, separados por un hueco.** No es sólo estética: el
       hueco es por donde se ve la fotografía. Con los paneles pegados a los bordes
       y entre sí, el fondo sólo asomaba a través de sus transparencias, y eso —tras
       multiplicar tres capas— no era nada. Ahora hay un marco real por el que mirar.

       Y le sienta bien a la transición: la tarjeta del acceso ya es una lámina
       redondeada flotando sobre la foto, así que al convertirse en el menú no tiene
       que perder sus esquinas ni pegarse a un borde — sólo cambia de sitio y de
       tamaño. */
    <div
      className={`caja-de-pantalla flex h-full gap-2 p-2 sm:gap-3 sm:p-3 ${claro ? "apariencia-clara" : ""}`}
    >
      {/* Enlace de salto: primer elemento tabulable de la página (WCAG 2.4.1).
          Sin él, quien navega con teclado atraviesa toda la barra lateral en
          cada carga antes de llegar al contenido. */}
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-[var(--radius-control)] focus:px-3 focus:py-2"
        style={{ backgroundColor: "var(--primary)", color: "var(--primary-fg)" }}
      >
        Saltar al contenido
      </a>

      {/* ⚠ **El menú y el encabezado no se imprimen, y el envoltorio existe para
          poder decirlo.** `.no-print` tiene que ir en un elemento cuyo `display` no
          le importe a nadie más: puesto sobre la propia barra, se pelearía con el
          `flex` que la maqueta en pantalla. */}
      {ancha ? (
        <div className="no-print contents">
          <BarraLateral />
        </div>
      ) : (
        <MenuMovil abierto={menuAbierto} claro={claro} onCerrar={() => setMenuAbierto(false)} />
      )}

      <div className="caja-de-pantalla flex min-w-0 flex-1 flex-col gap-2 sm:gap-3">
        {/* ⚠ **El encabezado y el contenido esperan a que el menú se forme.** La
            lámina del acceso tarda 0,62 s en convertirse en barra lateral; sin esta
            espera, el sistema entero aparece de golpe mientras la tarjeta todavía
            viaja, y lo que se ve es una tarjeta llegando tarde a un sitio que ya
            está ocupado. El compás vive en `lib/transiciones.ts`.

            `initial` y `animate` y no una clase: esto ocurre UNA vez, al montar el
            armazón, y montar el armazón es exactamente entrar al sistema. */}
        <motion.div
          className="no-print overflow-hidden rounded-[18px] shadow-[0_10px_30px_-16px_rgb(12_4_8/0.55)]"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...ENTRADA.encabezado, ease: [0.16, 1, 0.3, 1] }}
        >
          <Encabezado
            onAbrirMenu={ancha ? undefined : () => setMenuAbierto(true)}
            menuAbierto={menuAbierto}
          />
        </motion.div>
        {/* ⚠ **Literalmente la misma clase que el menú y el encabezado.** Tuvo una
            propia con el mismo color a otra opacidad, y el armazón salía de dos
            tonos: sobre una fotografía, el mismo vino al 0,34 y al 0,55 no son dos
            intensidades de un color, son dos colores. Una superficie, una clase.

            ⚠ **`paleta-oscura` va con las fotografías, y sólo con ellas.** Sobre
            este cristal oscuro, el contenido tiene que pintarse con la paleta oscura
            o saldría texto oscuro sobre fondo oscuro. En el modo claro no hay foto
            ni cristal oscuro —`.apariencia-clara` vuelve blanco el material—, así
            que la clase se quita y el área sigue al tema de la raíz.

            ⚠ **El `overflow` vive en el hijo, no aquí.** Este elemento lleva el
            radio, y una barra de desplazamiento en el mismo sitio se pinta sobre la
            esquina redondeada — la misma franja recta cortando la curva que ya hubo
            que arreglar en el menú. */}
        <motion.main
          id="contenido"
          className={`caja-de-pantalla marco-cristal ${claro ? "" : "paleta-oscura"} min-h-0 flex-1 overflow-hidden rounded-[18px] shadow-[0_10px_30px_-16px_rgb(12_4_8/0.55)]`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ...ENTRADA.contenido, ease: "easeOut" }}
        >
          {/* 12 px en el teléfono y 16 desde `sm`: cada píxel de margen aquí se suma
              al de la tarjeta de dentro, y en 320 px eso es lo que no cabe. Y 80 px
              abajo mientras el menú es cajón: el botón de reportar flota en esa
              esquina y, sin el hueco, tapaba la última fila y sus botones. */}
          <div className="caja-de-pantalla barra-area h-full overflow-auto p-3 pb-20 sm:p-4 sm:pb-20 lg:pb-4">
            {/*
            ⚠ **El límite envuelve el contenido y NO el armazón**, y la diferencia
            importa: si envolviera todo, una pantalla caída se llevaría por delante la
            barra lateral y el botón de reportar — y quien está delante se quedaría
            sin la única forma de contar lo que acaba de pasar. Así el menú sigue ahí
            y el reporte sale con la señal ya registrada.

            ⚠ **Con `key` en la ruta.** Sin ella, una vez caído el límite se queda
            caído: React no lo reinicia solo, y navegar a otra pantalla seguiría
            enseñando el mensaje de error de la anterior.
          */}
            <LimiteDeError key={ubicacion.pathname}>
              {/* ⚠ **Fundido al cambiar de pantalla, y SÓLO de opacidad (12/10/2026).**
                  Con la misma `key` que el límite, cada ruta —también cada pestaña de
                  un departamento, que va en la URL— entra en 180 ms en vez de
                  aparecer de golpe. Sin `transform`: un ancestro transformado
                  atraparía cualquier `fixed` de la pantalla, que es justo el defecto
                  que obligó a sacar modales y cajones a un portal. Y sin salida: con
                  `AnimatePresence` la pantalla vieja se quedaría pintada mientras la
                  nueva ya pide sus datos. */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <Outlet />
              </motion.div>
            </LimiteDeError>
          </div>
        </motion.main>
      </div>

      {/* El reporte de fallas es del personal: el portal de transporte no lo alcanza. */}
      {!esTransportista(usuario) && <BotonReportar />}
      {/* Y a su lado el buscador de módulos (05/11), que decide solo a quién se enseña. */}
      <BuscadorModulos />
    </div>
  )
}
